/* eslint-disable react-hooks/exhaustive-deps */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { ValidateEmailResponse } from 'views/Authentication/services/validateEmailApi';
import { RegistrationResult } from 'views/Authentication/services/authRegisterApi';
import useRegistrationMutation from 'views/Authentication/hooks/useRegistrationMutation';
import { useValidateEmailQuery } from 'views/Authentication/hooks/useValidateEmailQuery';
import AuthLayout from '../AuthLayout';
import { LoginModelProp } from '../../types';
import { DisplayField, DisplayFieldType } from '../../../../components/DisplayForms';
import { Button } from '../../../../components';
import { buildDisplayText, classNames, validateEmail } from '../../../../utils';
import { getFieldAttributes, passwordChangeValidationTests } from '../../../../helpers';
import { BackArrow, SuccessTickIcon } from '../../../../assets/icons';
import DisplayForm from '../../../../components/DisplayForms/DisplayForm';

import { logError } from '../../../../components/LogError';
import { EmailValuationType } from '../../../../types/AuthType';
import { AppContext } from '../../../../context/ContextProvider';
import { NavigationPath } from '../../../../types/DomainTypes';

enum DisplayTextKeys {
  TITLE = 'title',
  SUBTITLE = 'subTitle',
  REGISTER_LINK = 'registerLink',
  FORGOT_PASSWORD_LINK = 'forgotPasswordLink',
  FAILED_TO_LOGIN = 'failedToLogin',
  FIND_OUT_MORE = 'findOutMore',
  SHOW = 'show',
  HIDE = 'hide',
  CTA = 'callToAction',
  EMAIL_TEXT = 'email.title',
  PASSWORD_TEXT = 'password.title',
  CONFIRM_PASSWORD_TEXT = 'confirmPassword.title',

  PASSWORD_INSTRUCTIONS = 'password_instructions',
  PASSWORD_INVALID_TEXT = 'password.invalid',
  PASSWORD_REQUIRED_TEXT = 'password.required',

  EMAIL_INVALID_TEXT = 'email.invalid',
  EMAIL_REQUIRED_TEXT = 'email.required',
  SUBMIT_BUTTON_TEXT = 'submit_button_text',
  INVALID_REGISTRATION_TEXT = 'invalid_registration_text',
  ALREADY_REGISTERED_TEXT = 'already_registered_text',
  VERIFY_BUTTON_TEXT = 'verify_button_text',
  ENROLMENT_ERROR_TEXT = 'enrolment_error_text',
}

enum RegistrationModelProp {
  EMAIL = 'email',
  PASSWORD = 'password',
  CONFIRM_PASSWORD = 'confirmpassword',
}

type Model = {
  [key in RegistrationModelProp]: string;
};

type FormValidationType = {
  [Key in keyof Model]: boolean;
};

const initialModel = {
  [`${RegistrationModelProp.EMAIL}`]: '',
  [`${RegistrationModelProp.PASSWORD}`]: '',
  [`${RegistrationModelProp.CONFIRM_PASSWORD}`]: '',
} as Model;

const validateRegistrationFormFunc = (value: string, modelKey: string) => {
  if (modelKey === RegistrationModelProp.EMAIL) {
    return validateEmail(value);
  }
  // if (modelKey === RegistrationModelProp.PASSWORD) {
  //   return value.length >= 8;
  // }
  return false;
};

const RegistrationForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    state: {
      app: { id: appId },
    },
  } = useContext(AppContext);

  const [isEmailValidationRequired, setIsEmailValidationRequired] = useState(false);
  const [viewState, setViewState] = useState({
    lastCheckEmail: '',
    capturePassword: false,
    lastEmailCheckResult: EmailValuationType.IN_VALID,
  });

  const [error, setError] = useState<Error | null>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [formValidation, setFormValidation] = useState<FormValidationType>({
    [RegistrationModelProp.PASSWORD]: false,
    [RegistrationModelProp.EMAIL]: false,
    [RegistrationModelProp.CONFIRM_PASSWORD]: false,
  });

  const displayText = React.useMemo(
    () => buildDisplayText(Object.values(DisplayTextKeys), 'auth:registrationForm', t),
    [],
  );
  const [model, setModel] = useState<Model>({
    [RegistrationModelProp.PASSWORD]: '',
    [RegistrationModelProp.CONFIRM_PASSWORD]: '',
    [RegistrationModelProp.EMAIL]: '',
  });

  const {
    data: emailValidatorResult,
    isFetching: emailValidatorValidating,
    error: emailValidatorError,
    refetch: emailValidator,
  } = useValidateEmailQuery(model[RegistrationModelProp.EMAIL]);

  const { data: registrationResult, error: enrolmentError, mutateAsync: onRegisterUser } = useRegistrationMutation();

  const onClear = (key: string) => {
    formik.setFieldValue(key, '');
    setFormValidation({ ...formValidation, [key]: false });
    updateModel(key, '');
  };

  const updateFormValidation = (value: string, modelKey: RegistrationModelProp) => {
    const validationResult = validateRegistrationFormFunc(value, modelKey);
    if (formValidation[modelKey] !== validationResult) {
      setFormValidation({ ...formValidation, [modelKey]: validationResult });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
    e.preventDefault();
    formik.handleChange(e);
    updateFormValidation(e.target.value, field!.modelKey as RegistrationModelProp);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
    formik.setFieldTouched(field!.name, true);
    updateFormValidation(e.target.value, field!.modelKey as RegistrationModelProp);
  };

  const mValidationSchema = yup.object({
    [RegistrationModelProp.PASSWORD]: yup
      .string()
      .test(
        LoginModelProp.PASSWORD,
        displayText[DisplayTextKeys.PASSWORD_INVALID_TEXT],
        (password) => (password || '').length === 0 || (password || '').length > 7,
      )
      .required(displayText[DisplayTextKeys.PASSWORD_REQUIRED_TEXT]),
    [RegistrationModelProp.EMAIL]: yup
      .string()
      .email(t('auth:emailAddress.required'))
      .required(t('auth:emailAddress.required')),
  });

  const formik = useFormik<Model>({
    initialValues: initialModel,
    onSubmit: async (values) => {
      setIsProcessing(true);
      const registration = {
        registeredByApp: `${appId}`,
        passwordConfirmation: values.confirmpassword,
        password: values.password,
        emailAddress: values.email,
      };
      onRegisterUser({ ...registration });
    },
    validationSchema: mValidationSchema || model,
  });

  const updateModel = (key: string, value: unknown) => {
    setModel({ ...model, [key]: value });
  };
  const onFieldUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
    handleChange(e, field);
    updateModel(field?.modelKey || '', e.target.value);
  };

  const onBack = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.preventDefault();
    navigate(NavigationPath.LOGIN);
  };

  const processEmailVerification = (result: string) => {
    switch (result.toLowerCase() as EmailValuationType) {
      case 'valid':
        setViewState({
          lastCheckEmail: model[RegistrationModelProp.EMAIL],
          capturePassword: true,
          lastEmailCheckResult: EmailValuationType.VALID,
        });

        break;
      case 'invalid':
        setViewState({
          lastCheckEmail: model[RegistrationModelProp.EMAIL],
          capturePassword: false,
          lastEmailCheckResult: EmailValuationType.IN_VALID,
        });
        setError(new Error(displayText[DisplayTextKeys.INVALID_REGISTRATION_TEXT]));
        logError('Invalid registration email address', { user: { email: model[RegistrationModelProp.EMAIL] } });
        break;

      case 'registered':
        setViewState({
          lastCheckEmail: model[RegistrationModelProp.EMAIL],
          capturePassword: false,
          lastEmailCheckResult: EmailValuationType.REGISTERED,
        });

        const errMsg = t('auth:registrationForm.already_registered_text').replace(
          '**loginUrl**',
          `${window.location.origin}${NavigationPath.LOGIN}`,
        );
        setError(new Error(errMsg));
        break;

      default:
        break;
    }
  };

  const signUpform = useMemo(() => {
    const emailHasError = formik.touched[RegistrationModelProp.EMAIL] && formik.errors[RegistrationModelProp.EMAIL];
    const emailAttrs = getFieldAttributes(
      RegistrationModelProp.EMAIL,
      RegistrationModelProp.EMAIL,
      displayText[DisplayTextKeys.EMAIL_TEXT],
    );
    const emailField = {
      ...emailAttrs,
      disabled: emailValidatorValidating || isProcessing,
      isAutoFocus: model[RegistrationModelProp.EMAIL].length === 0,
      inputProps: {
        showClearButton: true,
        inputClassName: 'bg-transparent',
        inputContainerClassName: emailHasError ? '!border-red' : '!border-gray-300',
      },
      // placeholder: displayText[DisplayTextKeys.EMAIL_REQUIRED_TEXT],
      onChange: onFieldUpdate,
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => {
        handleBlur(e, field);
      },
      onClear: (dField?: DisplayField) => {
        if (dField) onClear(dField.id);
        setViewState({ ...viewState, capturePassword: false });
      },
      helperText: emailHasError ? formik.errors[RegistrationModelProp.EMAIL] : '',
      label: {
        ...emailAttrs.label,
        className: classNames(`${emailAttrs.label}`, `${emailHasError ? 'text-red' : ''}`),
      },
    };

    const passwordFields: DisplayField[] = (['PASSWORD', 'CONFIRM_PASSWORD'] as const).map((field) => {
      const pwdHasError = formik.touched[RegistrationModelProp[field]] && formik.errors[RegistrationModelProp[field]];
      const pwdAttrs = getFieldAttributes(
        RegistrationModelProp[`${field}` as const],
        RegistrationModelProp[`${field}` as const],
        displayText[DisplayTextKeys[`${field}_TEXT` as const]],
      );
      return {
        ...pwdAttrs,
        disabled: emailValidatorValidating || isProcessing || !viewState.capturePassword,
        className: 'border-gray-500',
        type: DisplayFieldType.PASSWORD,
        // placeholder: t('auth:password.required'),
        inputProps: {
          showClearButton: true,
          inputClassName: 'bg-transparent',
          inputContainerClassName: '!border-gray-300',
        },
        helperText: pwdHasError ? formik.errors[RegistrationModelProp[`${field}`]] : '',
        onChange: onFieldUpdate,
        onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, fieldIn?: DisplayField) => {
          handleBlur(e, fieldIn);
        },
        onClear: (dField?: DisplayField) => {
          if (dField) onClear(dField.id);
        },
        label: {
          ...pwdAttrs.label,
          className: classNames(`${pwdAttrs.label}`, `${pwdHasError ? 'text-red' : ''}`),
        },
      };
    });

    const validations: Record<
      string,
      {
        isValid: boolean;
        text: string;
      }
    > = {
      ...passwordChangeValidationTests(
        model[RegistrationModelProp.PASSWORD],
        t,
        `account:settings.${DisplayTextKeys.PASSWORD_INSTRUCTIONS}` as const,
      ),
      sameTest: {
        isValid:
          model[RegistrationModelProp.PASSWORD].length > 0 &&
          model[RegistrationModelProp.PASSWORD] === model[RegistrationModelProp.CONFIRM_PASSWORD],
        text: t(`account:settings.sameTest`),
      },
    };

    const passwordContraints = {
      ...getFieldAttributes('passwordContraints', 'passwordContraints', ''),
      type: DisplayFieldType.CUSTOM,
      customTemplate: () => {
        return (
          <ul className="list-none">
            {Object.keys(validations).map((key, index) => {
              const test = validations[key];
              return (
                <li key={`pwd-info-${index}`} className="text-sm">
                  <div className="flex items-center">
                    <div className="mr-2">
                      {test.isValid ? <SuccessTickIcon /> : <div className="w-1 h-1 bg-black rounded-full" />}
                    </div>
                    <span> {`${test.text}`}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        );
      },
    };

    const isFormValid =
      !Object.keys(validations)
        .map((key) => validations[key].isValid)
        .includes(false) &&
      !emailValidatorValidating &&
      formValidation[RegistrationModelProp.EMAIL] &&
      viewState.capturePassword;

    const verifyButtonField = {
      id: 'verify_Button',
      name: 'verify_Button',
      type: DisplayFieldType.CUSTOM,
      containerClassName: 'mt-5',
      customTemplate: () => {
        return (
          <Button
            onClick={() => {
              if (model[RegistrationModelProp.EMAIL] !== viewState.lastCheckEmail) {
                setIsEmailValidationRequired(true);
              } else if (!viewState.capturePassword) {
                processEmailVerification(viewState.lastEmailCheckResult);
              }
            }}
            className={`w-full mt-10 mb-3 ${!formValidation.email ? 'btn-disabled' : 'btn-primary'}`}
            isDisable={!formValidation.email}
            isProcessing={emailValidatorValidating}
            props={{
              name: displayText[DisplayTextKeys.VERIFY_BUTTON_TEXT],
            }}
          >
            {displayText[DisplayTextKeys.VERIFY_BUTTON_TEXT]}
          </Button>
        );
      },
    };

    let visibleFields: DisplayField[] = [emailField];
    if (!viewState.capturePassword) visibleFields = [...visibleFields, verifyButtonField];
    if (viewState.capturePassword) visibleFields = [...visibleFields, ...passwordFields, passwordContraints];

    return {
      isValid: isFormValid,
      sections: [
        {
          className: 'w-full  gap-5',
          fields: visibleFields,
        },
      ],
    };
  }, [model, formik, emailValidatorValidating]);

  if (emailValidatorError) {
    logError(JSON.stringify(emailValidatorError));
  }

  useEffect(() => {
    if (formValidation.email && isEmailValidationRequired) {
      emailValidator();
      setIsEmailValidationRequired(false);
    }
    if (!formValidation.email) {
      setViewState({ ...viewState, capturePassword: false });
    }
  }, [formValidation.email, isEmailValidationRequired]);

  useEffect(() => {
    if (emailValidatorResult) {
      processEmailVerification((emailValidatorResult as ValidateEmailResponse).status);
    }
  }, [emailValidatorResult]);

  useEffect(() => {
    if (registrationResult) {
      const enrolmentCard = registrationResult.data as RegistrationResult;
      if (enrolmentCard && enrolmentCard.emailAddress && enrolmentCard.userId) {
        navigate(NavigationPath.LOGIN, {
          state: {
            email: model[RegistrationModelProp.EMAIL],
          },
        });
      }
    }
  }, [registrationResult]);

  if (enrolmentError) {
    logError(JSON.stringify(enrolmentError));
    setError(new Error(displayText[DisplayTextKeys.ENROLMENT_ERROR_TEXT]));
  }

  return (
    <AuthLayout
      title={
        <div id="title-container" className="flex items-center">
          <BackArrow className="mr-5 cursor-pointer" data-testid="back-arrow-button" onClick={onBack} />
          <h5 className="text-lg">{displayText[DisplayTextKeys.TITLE]}</h5>
        </div>
      }
      error={{ body: error?.message || '', onClose: () => setError(null) }}
      subTitle={displayText.subTitle}
    >
      <div className="w-full sm:w-[70%] max-w-[420px]">
        <DisplayForm
          titleContainerClassName="mt-10"
          sectionsContainerClassName="pb-10"
          sections={signUpform.sections}
          title=""
          model={{ ...model, modelType: 'registration' }}
        />
        {viewState.capturePassword && (
          <Button
            onClick={() => formik.submitForm()}
            isDisable={!signUpform.isValid}
            className={`w-full mt-10 mb-3 ${!signUpform.isValid ? 'btn-disabled' : 'btn-primary'}`}
            isProcessing={isProcessing}
            props={{
              name: 'register_button',
            }}
          >
            {displayText[DisplayTextKeys.SUBMIT_BUTTON_TEXT]}
          </Button>
        )}
      </div>
    </AuthLayout>
  );
};

export default RegistrationForm;
